import moment from 'moment';

export interface LinkRoomDetailsItem {
  ld_id: number;
  ld_company: string;
  ld_parent: number;
  ld_checkin: number;
  ld_room: number;
  ld_flag: string;
}

export interface LinkRoomsPayload {
  lr_id: number;
  lr_company: string;
  lr_date: string;
  lr_checkin: number;
  lr_room: number;
  lr_narr: string;
  lr_flag: string;
  lr_uid?: string;
  lr_cdt: string;
  lr_muid?: string;
  lr_mdt: string;
  lr_details: LinkRoomDetailsItem[];
}

export const calculateProjectedDeparture = (
  currentDeparture: string | Date | undefined,
  days: number,
): moment.Moment => {
  const current = currentDeparture ? moment(currentDeparture) : moment();
  const safeDays = isNaN(days) || days <= 0 ? 1 : days;
  return current.clone().add(safeDays, 'days');
};

export const buildLinkRoomsPayload = (params: {
  cid: string;
  masterRoom: { rm_id: number; ci_id: number };
  checkedRoomKeys: string[];
  activeLinkId?: number;
  remarks?: string;
  user?: string;
}): LinkRoomsPayload => {
  const { cid, masterRoom, checkedRoomKeys, activeLinkId = 0, remarks = '', user = '' } =
    params;

  const details: LinkRoomDetailsItem[] = checkedRoomKeys.map(key => {
    const parts = key.split('.');
    return {
      ld_id: 0,
      ld_company: cid,
      ld_parent: activeLinkId,
      ld_checkin: Number(parts[0]),
      ld_room: Number(parts[1]),
      ld_flag: '1',
    };
  });

  const now = moment().format('YYYY/MM/DD HH:mm:ss');

  return {
    lr_id: activeLinkId,
    lr_company: cid,
    lr_date: now,
    lr_checkin: masterRoom.ci_id,
    lr_room: masterRoom.rm_id,
    lr_narr: remarks.trim(),
    lr_flag: '1',
    lr_uid: user,
    lr_cdt: now,
    lr_muid: user,
    lr_mdt: now,
    lr_details: details,
  };
};

export const validateAuthorizationRequest = (params: {
  authType: string;
  value: string;
  remarks: string;
  lastCheckout?: any;
}): { valid: boolean; error?: string } => {
  const { authType, value, remarks, lastCheckout } = params;

  if (!remarks || !remarks.trim()) {
    return { valid: false, error: 'Please provide remarks / reason for this request' };
  }

  if (
    (authType === 'GraceTime' || authType === 'Discount' || authType === 'ReduceTariff') &&
    (!value || isNaN(Number(value)) || Number(value) <= 0)
  ) {
    return { valid: false, error: 'Please enter a valid amount / value' };
  }

  if (authType === 'RoomStatus' && !value) {
    return { valid: false, error: 'Please select a new room status' };
  }

  if (authType === 'OpenToRollBack' && !lastCheckout) {
    return { valid: false, error: 'No previous checkout bill found for rollback' };
  }

  return { valid: true };
};

export const buildAuthorizationPayload = (params: {
  room: { rm_id: number; ci_id?: number };
  authType: string;
  value: string;
  remarks: string;
  applyToAll?: boolean;
  lastCheckout?: any;
}): {
  au_ciid: number;
  au_rmid: number;
  au_type: string;
  au_value: string;
  au_remarks: string;
  apply_all: boolean;
} => {
  const { room, authType, value, remarks, applyToAll = false, lastCheckout } = params;

  const targetCiid =
    authType === 'OpenToRollBack' && lastCheckout?.cd_checkin
      ? Number(lastCheckout.cd_checkin)
      : Number(room.ci_id || 0);

  const targetValue =
    authType === 'OpenToRollBack' && lastCheckout?.co_no
      ? String(lastCheckout.co_no)
      : String(value || '');

  return {
    au_ciid: targetCiid,
    au_rmid: Number(room.rm_id || 0),
    au_type: authType,
    au_value: targetValue,
    au_remarks: remarks.trim(),
    apply_all: applyToAll,
  };
};
